import { useState } from 'react'
import { useTexte } from '../../texte'

import { useDunkel } from '../../hooks/useDunkel'
import {
  LEER,
  LISTENFARBEN,
  listenStil,
  type Listenfarbe,
  type Listensatz,
} from '../../lib/listenfarben'

/**
 * Drei Farbfelder für die Reiseübersicht — mit einer Vorschau aus zwei Zeilen.
 *
 * **Das sichtbare Stück von zwei Karten** (#54): der Voreinstellung in der
 * Verwaltung und der eigenen Einstellung am Konto. Beide zeigen dasselbe, und
 * sie sollen es weiter tun — dieselbe Überlegung wie bei `Blasenwahl`.
 *
 * **Leer heißt „eine Stufe höher".** Am Konto heißt das „wie die
 * Voreinstellung", in der Verwaltung „wie das Erscheinungsbild". Der Wert ist
 * derselbe; was er bedeutet, sagt die Aufschrift des Knopfes (`leerText`) und
 * der Vorspann — zwei Wörter für dieselbe Sache wären der Fehler, nicht die
 * Lösung.
 */
export function Listenwahl({
  lead,
  werte,
  ererbt,
  leerText,
  alleText,
  laeuft,
  fehler,
  gemeldet,
  onSpeichern,
}: {
  lead: string
  werte: Listensatz
  /**
   * **Was gilt, solange hier nichts steht** — für das Farbfeld und die
   * Vorschau. Am Konto ist das die Voreinstellung; in der Verwaltung gibt es
   * nichts darüber, und dann bleibt es beim eingebauten Wert.
   *
   * Aus dem Betrieb, an der Karte nebenan gemeldet: ein Farbfeld kennt kein
   * „leer" und zeigte deshalb eine Farbe, die nirgends galt.
   */
  ererbt?: Partial<Listensatz>
  /** Was der Knopf neben jeder Zeile heißt, der den Wert wieder wegräumt. */
  leerText: string
  /**
   * Und wie der Knopf heißt, der **alle drei** wegräumt.
   *
   * Als eigener Text und nicht aus `leerText` zusammengesetzt: „Alle
   * voreinstellung" war genau das Ergebnis davon, und in einer deutschen
   * Oberfläche steht so etwas nicht.
   */
  alleText: string
  laeuft: boolean
  fehler: string | null
  gemeldet: boolean
  onSpeichern: (werte: Listensatz) => Promise<void>
}) {
  const t = useTexte()
  const dunkel = useDunkel()
  const [entwurf, setEntwurf] = useState<Partial<Listensatz>>({})

  const gilt = (welche: Listenfarbe) => entwurf[welche] ?? werte[welche] ?? ''
  const jetzt = (): Listensatz => ({
    own: gilt('own'),
    other: gilt('other'),
    bg: gilt('bg'),
  })
  const geaendert = LISTENFARBEN.some((e) => gilt(e.key) !== (werte[e.key] ?? ''))

  function setze(welche: Listenfarbe, wert: string) {
    setEntwurf((alt) => ({ ...alt, [welche]: wert }))
  }

  /**
   * Die Vorschau zeigt dasselbe wie das Farbfeld daneben — auch das Geerbte.
   *
   * **Und sie rechnet wie die Liste** (#73): im dunklen Erscheinungsbild wird
   * die Farbe umgerechnet und die Schrift dazu bestimmt. Hier stand nur
   * `{ background: farbe }`; die Schrift kam von der Seite, und im Dunkeln
   * waren das weiße Buchstaben auf der hell gebliebenen Wahl — gemessen
   * **1,29:1**. Dieselbe Rechnung wie in `listenVariablen()`, damit Vorschau
   * und Ergebnis nicht auseinanderlaufen können.
   */
  function vorschau(welche: Listenfarbe) {
    const farbe = gilt(welche) === '' ? (ererbt?.[welche] ?? '') : gilt(welche)
    return listenStil(farbe, dunkel)
  }

  return (
    <>
      <p className="card__lead" style={{ marginBottom: 10 }}>
        {lead}
      </p>

      {LISTENFARBEN.map((eintrag) => (
        <div key={eintrag.key} className="chatfarbe">
          <input
            type="color"
            className="kartenregler__farbe"
            name={eintrag.einstellung}
            aria-label={t.farben.listenFelder[eintrag.key].titel}
            // Ein Farbfeld kennt kein „leer". Steht nichts, zeigt es die
            // Farbe, die gerade gilt -- und die weiß nur der Browser.
            value={
              gilt(eintrag.key) === ''
                ? (ererbt?.[eintrag.key] || eintrag.vorgabe)
                : gilt(eintrag.key)
            }
            onChange={(event) => setze(eintrag.key, event.target.value)}
          />
          <span className="chatfarbe__text">
            <strong>{t.farben.listenFelder[eintrag.key].titel}</strong>
            <span className="chatfarbe__hinweis">{t.farben.listenFelder[eintrag.key].hinweis}</span>
          </span>
          <button
            type="button"
            className="button button--outline button--small"
            disabled={gilt(eintrag.key) === ''}
            onClick={() => setze(eintrag.key, '')}
          >
            {leerText}
          </button>
        </div>
      ))}

      {/* Zwei Zeilen auf der freien Fläche — so, wie sie später untereinander
          stehen. Als eigene Angabe und nicht über die Variablen am
          Wurzelelement: solange nicht gespeichert ist, soll sich nur dieses
          Beispiel ändern und nicht die halbe Anwendung. */}
      <div
        className="listenvorschau"
        style={vorschau('bg')}
      >
        <span
          className="listenvorschau__zeile listenvorschau__zeile--eigen"
          style={vorschau('own')}
        >
          {t.farben.listeBeispielEigen}
        </span>
        <span
          className="listenvorschau__zeile"
          style={vorschau('other')}
        >
          {t.farben.listeBeispielFremd}
        </span>
      </div>

      {fehler !== null && (
        <p className="notice" role="alert">
          {fehler}
        </p>
      )}
      {gemeldet && !laeuft && !geaendert && (
        <p className="card__lead" style={{ color: 'var(--accent)' }}>{t.allgemein.gespeichert}</p>
      )}

      <div className="chatfarbe__knoepfe">
        <button
          className="button button--outline"
          type="button"
          disabled={!geaendert || laeuft}
          onClick={() => {
            void onSpeichern(jetzt())
              .then(() => setEntwurf({}))
              .catch(() => undefined)
          }}
        >
          {laeuft ? t.allgemein.laeuft : t.allgemein.speichern}
        </button>

        <button
          className="button button--outline"
          type="button"
          disabled={
            laeuft || LISTENFARBEN.every((e) => gilt(e.key) === LEER[e.key])
          }
          onClick={() => setEntwurf({ ...LEER })}
        >
          {alleText}
        </button>
      </div>
    </>
  )
}
